import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { formatDateTime } from "@/lib/dateFormat";
import { ragApi, Batch, BatchDocument, BatchStatus } from "@/services/api/rag";

export const Route = createFileRoute("/portal/knowledgebase-file/batches/$id")({
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { id } = Route.useParams();
  const { token } = useAuth();
  usePortalData();
  const { can } = usePermissions();
  const canEditKbFile = can("edit", "kb-file");

  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [failedDocs, setFailedDocs] = useState<BatchDocument[]>([]);
  const [failedTotal, setFailedTotal] = useState(0);

  useEffect(() => {
    if (id && token) {
      loadBatch();

      // Poll for updates every 3 seconds if batch is processing
      const interval = setInterval(() => {
        if (
          batch &&
          (batch.status === "processing" || batch.status === "pending")
        ) {
          loadBatch(true);
        }
      }, 3000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, batch?.status]);

  const loadBatch = async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const response = await ragApi.getBatch(token, id);
      setBatch(response);
      // Load failed documents if there are any
      if (response.progress.failed > 0) {
        loadFailedDocuments();
      } else {
        setFailedDocs([]);
        setFailedTotal(0);
      }
    } catch (error) {
      console.error("Failed to load batch:", error);
      if (!silent) {
        toast.error("Failed to load batch", {
          description: "Could not fetch batch details",
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadFailedDocuments = async () => {
    if (!token) return;
    try {
      const response = await ragApi.getBatchDocuments(token, id, {
        stage: "failed",
        limit: 100,
      });
      setFailedDocs(response.documents);
      setFailedTotal(response.total);
    } catch (error) {
      console.error("Failed to load failed documents:", error);
    }
  };

  const handleRetryFailed = async () => {
    if (!token) return;
    setRetrying(true);
    try {
      const response = await ragApi.retryFailedDocuments(
        token,
        id,
      );
      toast.success("Retry queued", {
        description: `${response.retried_count} documents queued for retry`,
      });
      loadBatch();
    } catch (error) {
      console.error("Failed to retry documents:", error);
      toast.error("Retry failed", {
        description: "Could not retry failed documents",
      });
    } finally {
      setRetrying(false);
    }
  };

  const getStatusBadge = (status: BatchStatus) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="success">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case "processing":
        return (
          <Badge variant="default">
            <Clock className="mr-1 h-3 w-3" />
            Processing
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline">
            <XCircle className="mr-1 h-3 w-3" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStageBadge = (stage: string) => {
    switch (stage) {
      case "completed":
        return <Badge variant="success">Completed</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "extracting":
      case "embedding":
      case "saving":
        return <Badge variant="default">{stage}</Badge>;
      case "pending":
      case "queued":
        return <Badge variant="secondary">{stage}</Badge>;
      default:
        return <Badge variant="outline">{stage}</Badge>;
    }
  };

  const formatDateOrDash = (dateString: string | null): string => {
    if (!dateString) return "-";
    return formatDateTime(dateString);
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return "-";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600)
      return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  if (loading && !batch) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Link to="/portal/knowledgebase-file/batches">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Batches
          </Button>
        </Link>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Batch not found</AlertTitle>
          <AlertDescription>
            Could not load batch details. The batch may not exist.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/portal/knowledgebase-file/batches">
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{batch.batch_name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Batch ID: {batch.batch_id}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => loadBatch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canEditKbFile && (batch.status === "pending" ||
            batch.status === "failed" ||
            batch.progress.failed > 0) && (
            <Button size="sm" onClick={handleRetryFailed} disabled={retrying}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {retrying
                ? "Retrying..."
                : batch.progress.failed > 0
                  ? `Retry Failed (${batch.progress.failed})`
                  : "Retry Batch"}
            </Button>
          )}
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>{getStatusBadge(batch.status)}</CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(batch.progress.percentage)}%
            </div>
            <Progress value={batch.progress.percentage} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{batch.progress.total}</div>
            <p className="text-xs text-muted-foreground">
              {batch.progress.completed} completed &bull;{" "}
              {batch.progress.failed} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Est. Time Remaining
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {batch.estimated_completion_seconds
                ? formatDuration(batch.estimated_completion_seconds)
                : "-"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Batch Details */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Batch Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Batch Name
              </div>
              <div className="mt-1">{batch.batch_name}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Created At
              </div>
              <div className="mt-1">{formatDateOrDash(batch.created_at)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Started At
              </div>
              <div className="mt-1">{formatDateOrDash(batch.started_at)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Completed At
              </div>
              <div className="mt-1">{formatDateOrDash(batch.completed_at)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Processing Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Pending</span>
              </div>
              <Badge variant="secondary">{batch.progress.pending}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Processing</span>
              </div>
              <Badge variant="default">
                {batch.progress.processing}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm">Completed</span>
              </div>
              <Badge variant="success">
                {batch.progress.completed}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm">Failed</span>
              </div>
              <Badge variant="destructive">
                {batch.progress.failed}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Failed Documents Table */}
      {batch.progress.failed > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Failed Documents ({failedTotal})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {failedDocs.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedDocs.map((doc) => (
                    <TableRow key={doc.document_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate max-w-[200px]">
                            {doc.document_name || doc.document_id.substring(0, 8) + "..."}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{getStageBadge(doc.stage)}</TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {doc.attempts}/{doc.max_attempts}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-red-600 line-clamp-2">
                          {doc.last_error || "-"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">Loading failed documents...</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Auto-refresh Notice */}
      {(batch.status === "processing" || batch.status === "pending") && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertTitle>Auto-Refresh Enabled</AlertTitle>
          <AlertDescription>
            This page automatically refreshes every 3 seconds while the batch is
            processing.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
